/**
 * Componentes administrativos con lazy loading para optimización de rendimiento
 */

import React, { Suspense } from 'react';

import LoadingSpinner from '../ui/LoadingSpinner';
import PackManagerFallback from './PackManagerFallback';

// Función helper para lazy loading con retry
const lazyWithRetry = (componentImport: () => Promise<any>) => {
  return React.lazy(() =>
    componentImport().catch((error) => {
      console.warn('Error loading component, retrying...', error);
      // Retry después de un breve delay
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(componentImport());
        }, 1000);
      });
    })
  );
};

// Lazy loading de componentes administrativos con retry
const AdminOverview = lazyWithRetry(() => import('./AdminOverview'));
const UserManagement = lazyWithRetry(() => import('./UserManagement'));
const WebIDManager = lazyWithRetry(() => import('./WebIDManager'));
const AdminAnalytics = lazyWithRetry(() => import('./AdminAnalytics'));
const AdminSettings = lazyWithRetry(() => import('./AdminSettings'));
const CouponManager = lazyWithRetry(() => import('./CouponManager'));
const PackManager = lazyWithRetry(() => import('./PackManager'));
const ShippingManager = lazyWithRetry(() => import('./ShippingManager'));
const PackageGenerator = lazyWithRetry(() => import('./PackageGenerator'));
const TrackingDashboard = lazyWithRetry(() => import('./TrackingDashboard'));
const AuditLogs = lazyWithRetry(() => import('./AuditLogs'));

// Error Boundary para componentes lazy
class LazyErrorBoundary extends React.Component<
  { children: React.ReactNode; componentName?: string },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode; componentName?: string }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('LazyComponent Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col justify-center items-center h-64 p-4">
          <div className="text-red-500 text-lg mb-2">
            Error cargando {this.props.componentName || 'componente'}
          </div>
          <div className="text-gray-600 text-sm mb-4">
            {this.state.error?.message || 'Error desconocido'}
          </div>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            Recargar página
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Componente wrapper con Suspense para manejo de carga
interface LazyComponentWrapperProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  componentName?: string;
}

const LazyComponentWrapper: React.FC<LazyComponentWrapperProps> = ({
  children,
  fallback,
  componentName
}) => (
  <LazyErrorBoundary componentName={componentName}>
    <Suspense
      fallback={
        fallback || (
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner size="lg" message="Cargando componente..." />
          </div>
        )
      }
    >
      {children}
    </Suspense>
  </LazyErrorBoundary>
);

// Componentes lazy exportados con wrapper
export const LazyAdminOverview: React.FC = () => (
  <LazyComponentWrapper>
    <AdminOverview />
  </LazyComponentWrapper>
);

export const LazyUserManagement: React.FC = () => (
  <LazyComponentWrapper 
    fallback={
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" message="Cargando gestión de usuarios..." />
      </div>
    }
  >
    <UserManagement />
  </LazyComponentWrapper>
);

export const LazyWebIDManager: React.FC = () => (
  <LazyComponentWrapper
    fallback={
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" message="Cargando gestión de WebIDs..." />
      </div>
    }
  >
    <WebIDManager />
  </LazyComponentWrapper>
);

export const LazyAdminAnalytics: React.FC = () => (
  <LazyComponentWrapper
    fallback={
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" message="Cargando analytics..." />
      </div>
    }
  >
    <AdminAnalytics />
  </LazyComponentWrapper>
);

export const LazyAdminSettings: React.FC = () => (
  <LazyComponentWrapper
    fallback={
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" message="Cargando configuración..." />
      </div>
    }
  >
    <AdminSettings />
  </LazyComponentWrapper>
);

export const LazyCouponManager: React.FC = () => (
  <LazyComponentWrapper
    fallback={
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" message="Cargando gestión de cupones..." />
      </div>
    }
  >
    <CouponManager />
  </LazyComponentWrapper>
);

export const LazyPackManager: React.FC = () => {
  const [hasError, setHasError] = React.useState(false);

  if (hasError) {
    return <PackManagerFallback />;
  }

  return (
    <LazyComponentWrapper
      componentName="Gestión de Packs"
      fallback={
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" message="Cargando gestión de packs..." />
        </div>
      }
    >
      <React.Suspense
        fallback={
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner size="lg" message="Cargando gestión de packs..." />
          </div>
        }
      >
        <PackManagerWithErrorHandling onError={() => setHasError(true)} />
      </React.Suspense>
    </LazyComponentWrapper>
  );
};

// Wrapper para PackManager con manejo de errores
const PackManagerWithErrorHandling: React.FC<{ onError: () => void }> = ({ onError }) => {
  React.useEffect(() => {
    const handleChunkError = (event: ErrorEvent) => {
      if (event.message?.includes('Loading chunk') || event.message?.includes('PackManager')) {
        console.error('Chunk loading error detected:', event);
        onError();
      }
    };

    window.addEventListener('error', handleChunkError);
    return () => window.removeEventListener('error', handleChunkError);
  }, [onError]);

  try {
    return <PackManager />;
  } catch (error) {
    console.error('PackManager error:', error);
    onError();
    return <PackManagerFallback />;
  }
};

export const LazyShippingManager: React.FC = () => (
  <LazyComponentWrapper
    fallback={
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" message="Cargando gestión de envíos..." />
      </div>
    }
  >
    <ShippingManager />
  </LazyComponentWrapper>
);

export const LazyPackageGenerator: React.FC = () => (
  <LazyComponentWrapper
    fallback={
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" message="Cargando generador de paquetes..." />
      </div>
    }
  >
    <PackageGenerator />
  </LazyComponentWrapper>
);

export const LazyTrackingDashboard: React.FC = () => (
  <LazyComponentWrapper
    fallback={
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" message="Cargando dashboard de seguimiento..." />
      </div>
    }
  >
    <TrackingDashboard />
  </LazyComponentWrapper>
);

export const LazyAuditLogs: React.FC = () => (
  <LazyComponentWrapper
    fallback={
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" message="Cargando logs de auditoría..." />
      </div>
    }
  >
    <AuditLogs />
  </LazyComponentWrapper>
);

// Hook para precargar componentes administrativos
export const usePreloadAdminComponents = () => {
  React.useEffect(() => {
    // Precargar componentes más utilizados después de un delay
    const preloadTimer = setTimeout(() => {
      import('./AdminOverview');
      import('./UserManagement');
      import('./AdminAnalytics');
    }, 2000);

    return () => clearTimeout(preloadTimer);
  }, []);
};

// Componente para mostrar estadísticas de rendimiento
export const AdminPerformanceStats: React.FC = () => {
  const [stats, setStats] = React.useState({
    loadTime: 0,
    componentsLoaded: 0,
    memoryUsage: 0
  });

  React.useEffect(() => {
    const startTime = performance.now();
    
    const updateStats = () => {
      const loadTime = performance.now() - startTime;
      const memoryUsage = ('memory' in performance && (performance as Performance & { memory?: { usedJSHeapSize: number } }).memory?.usedJSHeapSize) || 0;
      
      setStats({
        loadTime: Math.round(loadTime),
        componentsLoaded: document.querySelectorAll('[data-admin-component]').length,
        memoryUsage: Math.round(memoryUsage / 1024 / 1024) // MB
      });
    };

    // Actualizar estadísticas cada 5 segundos
    const interval = setInterval(updateStats, 5000);
    updateStats(); // Actualización inicial

    return () => clearInterval(interval);
  }, []);

  // Solo mostrar en desarrollo
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  return (
    <div className="fixed bottom-4 right-4 bg-black bg-opacity-75 text-white text-xs p-2 rounded">
      <div>Load: {stats.loadTime}ms</div>
      <div>Components: {stats.componentsLoaded}</div>
      <div>Memory: {stats.memoryUsage}MB</div>
    </div>
  );
};
